import { useEffect, useRef, useState } from "react";
import { api, errorText } from "@/lib/api";
import { readSlate, writeSlate } from "@/lib/cache";

// Slate data for a day + live scores/box (30s while games are under way).
// league: "nba" (default) uses /projections + /live; "E"/"U" use the Euro projections endpoint.
export function useSlate(day, league = "nba") {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [livePlayers, setLivePlayers] = useState({});
  const gamesRef = useRef([]);
  gamesRef.current = data?.games || [];
  const euro = league !== "nba";

  useEffect(() => {
    let alive = true;
    const cacheKey = euro ? `${league}:${day}` : day;
    setData(euro ? null : readSlate(day)); setError(""); setLivePlayers({});
    const load = () => {
      const req = euro
        ? api.get("/euro/projections", { params: { comp: league, day } })
        : api.get("/projections", { params: { day } });
      return req.then((r) => { if (alive) { setData(r.data); writeSlate(cacheKey, r.data); } })
        .catch((e) => alive && setError(errorText(e)));
    };
    load();
    if (euro) return () => { alive = false; };  // Euro slates are historical/final: no live polling
    const tick = () => {
      const games = gamesRef.current;
      if (!games.some((g) => g.status === "in" || (g.status === "pre" && new Date(g.date_utc) <= new Date()))) return;
      api.get("/live", { params: { day } }).then((r) => {
        if (!alive) return;
        const live = Object.fromEntries(r.data.games.map((g) => [g.id, g]));
        setLivePlayers(r.data.players || {});
        if (games.some((g) => g.status !== "post" && live[g.id]?.status === "post")) load();
        else setData((d) => d && { ...d, games: d.games.map((g) => (live[g.id] ? { ...g, ...live[g.id] } : g)) });
      }).catch(() => {});
    };
    const first = setTimeout(tick, 1500);
    const timer = setInterval(tick, 30000);
    return () => { alive = false; clearTimeout(first); clearInterval(timer); };
  }, [day, league, euro]);

  return { data, error, livePlayers };
}
